import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";

/**
 * @typedef {object} T_AlphaPickerResult
 * @property {number} alphaFrom
 * @property {number} alphaTo
 */

/**
 * Layer Alpha-Channel Value Picker
 */
export default class AlphaPicker extends Dialog {
	/**
	 * @param {boolean} [rangeMode]
	 */
	constructor(rangeMode = false) {
		super({
			title: i18n('title.alpha-picker'),
			smallWidth: true,
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		this._rangeMode = rangeMode;

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(res: T_AlphaPickerResult|null)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * @returns {Promise<T_AlphaPickerResult|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * Handles click by button
	 *
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * Applies form data and destroy Dialog Box if all ok
	 *
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._onComplete) {
			this._onComplete({
				alphaFrom: parseInt(data.alphaFrom) / 100,
				alphaTo: parseInt(this._rangeMode ? data.alphaTo : data.alphaFrom) / 100,
			});
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * Builds form
	 *
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {alphaFrom: 100, alphaTo: 100},
			fields: [
				{
					type: 'range',
					name: 'alphaFrom',
					min: 10,
					max: 100,
					step: 5,
					caption: i18n(this._rangeMode ? 'inputs.layer.alpha-from' : 'inputs.layer.alpha'),
				},
				{
					type: this._rangeMode ? 'range' : 'hidden',
					name: 'alphaTo',
					min: 10,
					max: 100,
					step: 5,
					caption: i18n('inputs.layer.alpha-to'),
				},
			]
		});
		this.setContent(el);
	}

	/**
	 * Destroys the dialog box
	 *
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
